import { isDefined } from "@clipboard-health/util-ts";
import pluralize from "pluralize";

import {
  type ApplicableWorkplacesDetails,
  getApplicableWorkplacesTextForRequirement,
} from "./getApplicableWorkplacesTextForRequirement";

interface ApplicableWorkplacesDisplayTextParams {
  applicableWorkplacesDetails: ApplicableWorkplacesDetails | undefined;
  isSingleWorkplace: boolean;
  workplaceName: string | undefined;
}

/**
 * Resolves display text for the workplaces a requirement applies to.
 * Handles the single-workplace case by returning the workplace name directly,
 * otherwise delegates to {@link getApplicableWorkplacesTextForRequirement}.
 */
export function getApplicableWorkplacesDisplayText(
  params: ApplicableWorkplacesDisplayTextParams
): string | undefined {
  const { applicableWorkplacesDetails, isSingleWorkplace, workplaceName } = params;

  if (applicableWorkplacesDetails?.type === "WORKPLACE" && isSingleWorkplace) {
    return workplaceName;
  }

  if (!isDefined(applicableWorkplacesDetails)) {
    return undefined;
  }

  return getApplicableWorkplacesTextForRequirement(applicableWorkplacesDetails, false);
}

/**
 * V2 display text with more descriptive labels.
 * Returns "at all Clipboard Health workplaces" for global requirements
 * instead of "Clipboard".
 */
export function getApplicableWorkplacesDisplayTextV2(
  params: ApplicableWorkplacesDisplayTextParams
): string | undefined {
  const { applicableWorkplacesDetails, isSingleWorkplace, workplaceName } = params;

  if (!isDefined(applicableWorkplacesDetails)) {
    return undefined;
  }

  const { type, place, count } = applicableWorkplacesDetails;

  if (type === "ALL") {
    return "at all Clipboard Health workplaces";
  }

  if (type === "PLACE" && isDefined(place)) {
    return `at all workplaces in ${place}`;
  }

  if (type === "WORKPLACE" && isSingleWorkplace) {
    return `at ${workplaceName}`;
  }

  if (type === "WORKPLACE" && isDefined(count)) {
    return `at ${count} ${pluralize("workplace", count)}`;
  }

  return undefined;
}
